import { defineStore } from 'pinia'
import api from '@/services/axiosApi'
import { klona } from 'klona'
import type {
  RightyThread,
  RightyMessage,
  RightyAction,
  RightyInvocationContext,
  RightyNestedContext,
  CreateThreadPayload,
  SendMessagePayload,
  RecentCreation,
  RightyPrompt
} from '../types'
import type { ChangelogItem } from '../services/planExecutionService'
import { isSameContext } from '../services/rightyContextBuilder'
import { getDrawerSuggestions, getExternalChipPrompts } from '../services/promptUtilities'
import { useConfigStore } from '@/stores/configStore'
import type { Config } from '@/types/config'

export interface ToolCall {
  name: string
  status: 'pending' | 'running' | 'completed' | 'error'
  description?: string
  arguments?: Record<string, any>
  error?: string
}

// SSE Event types from the streaming endpoint
interface SSEEvent {
  type: string
  data: Record<string, any>
}

// Parse SSE events from buffer
function parseSSEEvents(buffer: string): { parsed: SSEEvent[]; remainder: string } {
  const events: SSEEvent[] = []
  const parts = buffer.split('\n\n')

  // Last part may be incomplete, keep it as remainder
  const remainder = parts.pop() || ''

  for (const part of parts) {
    if (!part.trim()) continue

    let eventType = 'message'
    let eventData: Record<string, any> = {}

    const lines = part.split('\n')
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7)
      } else if (line.startsWith('data: ')) {
        try {
          eventData = JSON.parse(line.slice(6))
        } catch {
          eventData = { raw: line.slice(6) }
        }
      }
    }

    events.push({ type: eventType, data: eventData })
  }

  return { parsed: events, remainder }
}

export const useRightyStore = defineStore('righty', () => {
  const route = useRoute()

  // State
  const isDrawerOpen = ref(false)
  const isFloatingUiHidden = ref(false) // Temporarily hides the floating button and context prompts
  const threads = ref<RightyThread[]>([])
  const currentThread = ref<RightyThread | null>(null)
  const messages = ref<RightyMessage[]>([])
  const isLoading = ref(false)
  const isStreaming = ref(false)
  const streamingContent = ref('')
  const streamingStartTime = ref<number | null>(null) // Timestamp when streaming started
  const currentPhase = ref<1 | 2 | null>(null) // Two-phase response tracking
  const isResearching = ref(false) // True when Phase 1 complete and Phase 2 starting
  const pendingAction = ref<RightyAction | null>(null)
  const error = ref<string | null>(null)
  const currentToolCalls = ref<ToolCall[]>([])
  const isReadyToChat = ref(false) // Shows suggestions UI without creating a thread yet
  const invocationContext = ref<RightyInvocationContext | null>(null)

  // Dynamic prompts registered by components (e.g., conditional prompts on VisitorDetail)
  const dynamicPrompts = ref<RightyPrompt[]>([])

  // Empty assistant state (generic - works for flows, emails, etc.)
  const syntheticWelcomeMessage = ref<RightyMessage | null>(null)
  const isEmptyAssistantActive = ref(false)
  const emptyAssistantContext = ref<{ type: string; id: string; name?: string } | null>(null)

  // Type-specific computed aliases
  const isEmptyFlowAssistantActive = computed(() =>
    isEmptyAssistantActive.value && emptyAssistantContext.value?.type === 'flow'
  )
  const isEmptyEmailAssistantActive = computed(() =>
    isEmptyAssistantActive.value && emptyAssistantContext.value?.type === 'email'
  )
  const emptyFlowContext = computed(() => {
    if (emptyAssistantContext.value?.type === 'flow') {
      return { flowId: emptyAssistantContext.value.id, flowName: emptyAssistantContext.value.name || '' }
    }
    return null
  })

  // Undo/changelog state for plan execution
  const configSnapshotBeforeBuild = ref<Config | null>(null)
  const lastBuildChangelog = ref<ChangelogItem[]>([])
  const canUndo = computed(() => configSnapshotBeforeBuild.value !== null)

  // Per-message built state and changelog tracking
  // This persists changelogs with their source messages so they stay in the thread
  const builtMessageChangelogs = ref<Map<number, ChangelogItem[]>>(new Map())

  // Computed
  const teamPid = computed(() => route.params.teamPid as string)
  const hasThreads = computed(() => threads.value.length > 0)
  const currentPage = computed(() => route.path)

  /**
   * Contextual suggestions for the drawer's "Try Asking" chips.
   * Always returns exactly 4 prompts, merging dynamic + route + defaults.
   */
  const contextualSuggestions = computed<RightyPrompt[]>(() => {
    return getDrawerSuggestions(dynamicPrompts.value, route)
  })

  /**
   * Contextual prompts for external chips (above the trigger button).
   * Returns up to 3 prompts, only if there are actual contextual prompts.
   */
  const externalChipPrompts = computed<RightyPrompt[]>(() => {
    return getExternalChipPrompts(dynamicPrompts.value, route)
  })

  // Actions
  function openDrawer() {
    isDrawerOpen.value = true
    // Automatically show the ready-to-chat state when opening
    if (!currentThread.value) {
      isReadyToChat.value = true
    }
  }

  function closeDrawer() {
    isDrawerOpen.value = false
  }

  function toggleDrawer() {
    isDrawerOpen.value = !isDrawerOpen.value
  }

  async function loadThreads() {
    if (!teamPid.value || teamPid.value === 'demo') return

    try {
      isLoading.value = true
      const { data } = await api.get(`/api/${teamPid.value}/righty/threads`)
      threads.value = data.threads
    } catch (err: any) {
      error.value = err.message || 'Failed to load threads'
      console.error('Failed to load threads:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function createThread(payload?: CreateThreadPayload): Promise<RightyThread | null> {
    if (!teamPid.value || teamPid.value === 'demo') return null

    try {
      isLoading.value = true
      const { data } = await api.post(`/api/${teamPid.value}/righty/threads`, payload || {})
      const newThread = data.thread as RightyThread
      threads.value.unshift(newThread)
      return newThread
    } catch (err: any) {
      error.value = err.message || 'Failed to create thread'
      console.error('Failed to create thread:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function selectThread(threadId: number) {
    if (!teamPid.value || teamPid.value === 'demo') return

    try {
      isLoading.value = true
      const { data } = await api.get(`/api/${teamPid.value}/righty/threads/${threadId}`)
      currentThread.value = data.thread
      messages.value = data.messages
      isReadyToChat.value = false // Clear ready state when selecting existing thread
    } catch (err: any) {
      error.value = err.message || 'Failed to load thread'
      console.error('Failed to load thread:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function deleteThread(threadId: number) {
    if (!teamPid.value || teamPid.value === 'demo') return

    try {
      await api.delete(`/api/${teamPid.value}/righty/threads/${threadId}`)
      threads.value = threads.value.filter((t) => t.id !== threadId)
      if (currentThread.value?.id === threadId) {
        currentThread.value = null
        messages.value = []
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to delete thread'
      console.error('Failed to delete thread:', err)
    }
  }

  async function sendMessage(message: string) {
    if (!teamPid.value || teamPid.value === 'demo') return

    // Capture synthetic message content before thread creation (for inclusion in payload)
    const syntheticMessageContent = isEmptyAssistantActive.value && syntheticWelcomeMessage.value
      ? syntheticWelcomeMessage.value.content
      : undefined

    // Lazily create thread if we're in ready-to-chat mode without a real thread
    if (!currentThread.value) {
      const thread = await createThread()
      if (!thread) {
        error.value = 'Failed to start conversation'
        return
      }
      currentThread.value = thread
      isReadyToChat.value = false

      // If empty assistant is active, add the synthetic welcome as a real message
      // This ensures the UI shows the conversation properly
      if (isEmptyAssistantActive.value && syntheticWelcomeMessage.value) {
        messages.value.push({
          ...syntheticWelcomeMessage.value,
          id: Date.now() - 1 // Give it a proper ID
        })
        // Clear the synthetic message since it's now a real message
        syntheticWelcomeMessage.value = null
      }
    }

    // Add optimistic user message
    const userMessage: RightyMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      createdAt: new Date().toISOString()
    }
    messages.value.push(userMessage)

    // Get recent creations for context (enables modify_conditional)
    const recentCreations = getRecentCreationsForContext()

    const payload: SendMessagePayload = {
      threadId: currentThread.value.id,
      message,
      currentPage: currentPage.value,
      context: invocationContext.value || undefined,
      recentCreations: recentCreations.length > 0 ? recentCreations : undefined,
      syntheticAssistantMessage: syntheticMessageContent
    }

    // Try SSE streaming first, fall back to non-streaming on error
    try {
      await sendMessageStreaming(payload, userMessage)
    } catch (err: any) {
      console.warn('Streaming failed, falling back to non-streaming:', err)
      try {
        await sendMessageNonStreaming(payload, userMessage)
      } catch (fallbackErr: any) {
        error.value = fallbackErr.message || 'Failed to send message'
        console.error('Failed to send message:', fallbackErr)
        // Remove optimistic message on error
        messages.value = messages.value.filter((m) => m.id !== userMessage.id)
      }
    }
  }

  /**
   * Send message using SSE streaming for real-time tool visibility.
   * Includes retry logic with exponential backoff for connection failures.
   */
  async function sendMessageStreaming(payload: SendMessagePayload, userMessage: RightyMessage) {
    const MAX_RETRIES = 3
    const BASE_DELAY_MS = 1000 // Start with 1 second delay
    let attempt = 0

    while (attempt < MAX_RETRIES) {
      attempt++
      try {
        await attemptStreamingRequest(payload, userMessage)
        return // Success - exit the retry loop
      } catch (err: any) {
        const isLastAttempt = attempt >= MAX_RETRIES
        const isRetryable = err.message?.includes('timed out') ||
                           err.message?.includes('network') ||
                           err.message?.includes('fetch') ||
                           err.name === 'TypeError' // Network errors often throw TypeError

        if (isLastAttempt || !isRetryable) {
          throw err // Propagate to fallback handler
        }

        // Exponential backoff: 1s, 2s, 4s
        const delayMs = BASE_DELAY_MS * Math.pow(2, attempt - 1)
        console.warn(`Streaming attempt ${attempt} failed, retrying in ${delayMs}ms:`, err.message)
        await new Promise((resolve) => setTimeout(resolve, delayMs))

        // Reset streaming state for retry
        streamingContent.value = ''
        currentToolCalls.value = []
      }
    }
  }

  /**
   * Single attempt at streaming request.
   * Tries two-phase endpoint first for faster response, falls back to single-phase on error.
   */
  async function attemptStreamingRequest(payload: SendMessagePayload, userMessage: RightyMessage) {
    isStreaming.value = true
    streamingContent.value = ''
    streamingStartTime.value = Date.now()
    currentPhase.value = null
    isResearching.value = false
    currentToolCalls.value = []
    error.value = null

    const xsrfToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1]
    const apiBaseUrl = import.meta.env.VITE_APP_BACKEND_HOST || ''
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-XSRF-TOKEN': xsrfToken ? decodeURIComponent(xsrfToken) : ''
    }

    // Try two-phase endpoint first for faster first response
    try {
      await executeTwoPhaseStreaming(apiBaseUrl, headers, payload)
      return // Success
    } catch (twoPhaseError: any) {
      console.warn('Two-phase streaming failed, falling back to single-phase:', twoPhaseError.message)
      // Reset state for fallback attempt
      streamingContent.value = ''
      currentPhase.value = null
      isResearching.value = false
      currentToolCalls.value = []
    }

    // Fallback to single-phase streaming (original chat-stream endpoint)
    await executeSinglePhaseStreaming(apiBaseUrl, headers, payload)
  }

  /**
   * Execute two-phase streaming request (fast first response, then tools if needed)
   */
  async function executeTwoPhaseStreaming(
    apiBaseUrl: string,
    headers: Record<string, string>,
    payload: SendMessagePayload
  ) {
    let lastEventTime = Date.now()
    const TIMEOUT_MS = 120000

    let phase1MessageId: number | null = null
    let phase1Content = ''

    const response = await fetch(`${apiBaseUrl}/api/${teamPid.value}/righty/chat-two-phase`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body reader available')
    }

    const decoder = new TextDecoder()
    let buffer = ''
    let savedMessageId: number | null = null

    const timeoutChecker = setInterval(() => {
      if (Date.now() - lastEventTime > TIMEOUT_MS) {
        reader.cancel()
        error.value = 'Connection timed out'
      }
    }, 5000)

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        lastEventTime = Date.now()
        buffer += decoder.decode(value, { stream: true })

        const { parsed, remainder } = parseSSEEvents(buffer)
        buffer = remainder

        for (const event of parsed) {
          switch (event.type) {
            case 'phase':
              currentPhase.value = event.data.phase as 1 | 2
              if (event.data.phase === 2) {
                if (phase1Content && phase1MessageId) {
                  const thinkingDuration = streamingStartTime.value
                    ? Math.round((Date.now() - streamingStartTime.value) / 1000)
                    : undefined
                  messages.value.push({
                    id: phase1MessageId,
                    role: 'assistant',
                    content: phase1Content,
                    createdAt: new Date().toISOString(),
                    metadata: { phase: 1, thinkingDuration }
                  })
                }
                streamingContent.value = ''
                isResearching.value = false
              }
              break

            case 'phase1_complete':
              phase1MessageId = event.data.messageId
              phase1Content = event.data.content || streamingContent.value
              streamingContent.value = phase1Content
              if (event.data.needsTools) {
                isResearching.value = true
              }
              break

            case 'thinking':
              break

            case 'text':
              if (event.data.content) {
                streamingContent.value += event.data.content
              }
              break

            case 'tool_start':
              currentToolCalls.value.push({
                name: event.data.name,
                status: 'running',
                arguments: event.data.arguments
              })
              break

            case 'tool_complete': {
              const toolIdx = currentToolCalls.value.findIndex(
                (t) => t.name === event.data.name && t.status === 'running'
              )
              if (toolIdx >= 0) {
                currentToolCalls.value[toolIdx].status = 'completed'
              }
              break
            }

            case 'tool_error': {
              const errorIdx = currentToolCalls.value.findIndex(
                (t) => t.name === event.data.name && t.status === 'running'
              )
              if (errorIdx >= 0) {
                currentToolCalls.value[errorIdx].status = 'error'
                currentToolCalls.value[errorIdx].error = event.data.error
              }
              break
            }

            case 'complete':
              break

            case 'message_saved':
              savedMessageId = event.data.messageId
              if (event.data.threadTitle && currentThread.value) {
                currentThread.value.title = event.data.threadTitle
              }
              break

            case 'error':
              throw new Error(event.data.message || 'Unknown streaming error')

            case 'heartbeat':
              break
          }
        }
      }
    } finally {
      clearInterval(timeoutChecker)
    }

    // Add the final assistant message
    if (streamingContent.value && currentPhase.value === 2) {
      messages.value.push({
        id: savedMessageId || Date.now() + 1,
        role: 'assistant',
        content: streamingContent.value,
        createdAt: new Date().toISOString(),
        metadata: {
          phase: 2,
          tool_calls: currentToolCalls.value.map((tc) => ({
            name: tc.name,
            arguments: tc.arguments
          }))
        }
      })
    } else if (streamingContent.value && !phase1MessageId) {
      const thinkingDuration = streamingStartTime.value
        ? Math.round((Date.now() - streamingStartTime.value) / 1000)
        : undefined
      messages.value.push({
        id: savedMessageId || phase1MessageId || Date.now() + 1,
        role: 'assistant',
        content: streamingContent.value,
        createdAt: new Date().toISOString(),
        metadata: { thinkingDuration }
      })
    }

    if (currentThread.value) {
      await selectThread(currentThread.value.id)
    }

    // Cleanup
    isStreaming.value = false
    streamingContent.value = ''
    streamingStartTime.value = null
    currentPhase.value = null
    isResearching.value = false
    setTimeout(() => {
      currentToolCalls.value = []
    }, 2000)
  }

  /**
   * Execute single-phase streaming request (fallback - original chat-stream endpoint)
   */
  async function executeSinglePhaseStreaming(
    apiBaseUrl: string,
    headers: Record<string, string>,
    payload: SendMessagePayload
  ) {
    let lastEventTime = Date.now()
    const TIMEOUT_MS = 60000

    const response = await fetch(`${apiBaseUrl}/api/${teamPid.value}/righty/chat-stream`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body reader available')
    }

    const decoder = new TextDecoder()
    let buffer = ''
    let savedMessageId: number | null = null

    const timeoutChecker = setInterval(() => {
      if (Date.now() - lastEventTime > TIMEOUT_MS) {
        reader.cancel()
        error.value = 'Connection timed out'
      }
    }, 5000)

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        lastEventTime = Date.now()
        buffer += decoder.decode(value, { stream: true })

        const { parsed, remainder } = parseSSEEvents(buffer)
        buffer = remainder

        for (const event of parsed) {
          switch (event.type) {
            case 'thinking':
              break

            case 'text':
              if (event.data.content) {
                streamingContent.value += event.data.content
              }
              break

            case 'tool_start':
              currentToolCalls.value.push({
                name: event.data.name,
                status: 'running',
                arguments: event.data.arguments
              })
              break

            case 'tool_complete': {
              const toolIdx = currentToolCalls.value.findIndex(
                (t) => t.name === event.data.name && t.status === 'running'
              )
              if (toolIdx >= 0) {
                currentToolCalls.value[toolIdx].status = 'completed'
              }
              break
            }

            case 'tool_error': {
              const errorIdx = currentToolCalls.value.findIndex(
                (t) => t.name === event.data.name && t.status === 'running'
              )
              if (errorIdx >= 0) {
                currentToolCalls.value[errorIdx].status = 'error'
                currentToolCalls.value[errorIdx].error = event.data.error
              }
              break
            }

            case 'complete':
              break

            case 'message_saved':
              savedMessageId = event.data.messageId
              if (event.data.threadTitle && currentThread.value) {
                currentThread.value.title = event.data.threadTitle
              }
              break

            case 'error':
              throw new Error(event.data.message || 'Unknown streaming error')

            case 'heartbeat':
              break
          }
        }
      }
    } finally {
      clearInterval(timeoutChecker)
    }

    // Add the assistant message
    if (streamingContent.value) {
      const thinkingDuration = streamingStartTime.value
        ? Math.round((Date.now() - streamingStartTime.value) / 1000)
        : undefined
      messages.value.push({
        id: savedMessageId || Date.now() + 1,
        role: 'assistant',
        content: streamingContent.value,
        createdAt: new Date().toISOString(),
        metadata: {
          thinkingDuration,
          tool_calls: currentToolCalls.value.map((tc) => ({
            name: tc.name,
            arguments: tc.arguments
          }))
        }
      })
    }

    if (currentThread.value) {
      await selectThread(currentThread.value.id)
    }

    // Cleanup
    isStreaming.value = false
    streamingContent.value = ''
    streamingStartTime.value = null
    currentPhase.value = null
    isResearching.value = false
    setTimeout(() => {
      currentToolCalls.value = []
    }, 2000)
  }

  /**
   * Send message using non-streaming JSON endpoint (fallback)
   */
  async function sendMessageNonStreaming(payload: SendMessagePayload, userMessage: RightyMessage) {
    try {
      isStreaming.value = true
      streamingContent.value = ''
      streamingStartTime.value = Date.now()
      currentToolCalls.value = []
      error.value = null

      const { data } = await api.post(`/api/${teamPid.value}/righty/chat`, payload)

      // Calculate thinking duration
      const thinkingDuration = streamingStartTime.value
        ? Math.round((Date.now() - streamingStartTime.value) / 1000)
        : undefined

      // Add the assistant message from response
      if (data.message) {
        const assistantMessage: RightyMessage = {
          id: data.message.id || Date.now() + 1,
          role: 'assistant',
          content: data.message.content,
          createdAt: data.message.createdAt || new Date().toISOString(),
          metadata: {
            ...data.message.metadata,
            thinkingDuration
          }
        }
        messages.value.push(assistantMessage)

        // Extract tool calls from metadata for UI display
        if (data.message.metadata?.toolCalls) {
          currentToolCalls.value = data.message.metadata.toolCalls.map(
            (tc: { name: string; arguments?: Record<string, any> }) => ({
              name: tc.name,
              status: 'completed' as const,
              arguments: tc.arguments
            })
          )
        }
      }

      // Refresh thread to get updated title and metadata
      if (currentThread.value) {
        await selectThread(currentThread.value.id)
      }
    } finally {
      isStreaming.value = false
      streamingContent.value = ''
      streamingStartTime.value = null
      // Clear tool calls after a short delay so user can see what was used
      setTimeout(() => {
        currentToolCalls.value = []
      }, 2000)
    }
  }

  async function sendMessageSync(message: string) {
    if (!teamPid.value || teamPid.value === 'demo' || !currentThread.value) return

    // Add optimistic user message
    const userMessage: RightyMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      createdAt: new Date().toISOString()
    }
    messages.value.push(userMessage)

    try {
      isLoading.value = true
      error.value = null

      // Get recent creations for context (enables modify_conditional)
      const recentCreations = getRecentCreationsForContext()

      const payload: SendMessagePayload = {
        threadId: currentThread.value.id,
        message,
        currentPage: currentPage.value,
        context: invocationContext.value || undefined,
        recentCreations: recentCreations.length > 0 ? recentCreations : undefined
      }

      const { data } = await api.post(`/api/${teamPid.value}/righty/chat-sync`, payload)

      // Replace optimistic message with real one
      const index = messages.value.findIndex((m) => m.id === userMessage.id)
      if (index !== -1) {
        messages.value.splice(index, 1)
      }

      // Add real messages
      messages.value.push({
        id: userMessage.id,
        role: 'user',
        content: message,
        createdAt: userMessage.createdAt
      })
      messages.value.push(data.message)

      // Update thread
      if (data.thread) {
        currentThread.value = { ...currentThread.value, ...data.thread }
      }

      // Check for actions
      if (data.message.metadata?.action) {
        pendingAction.value = data.message.metadata.action
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to send message'
      console.error('Failed to send message:', err)
      // Remove optimistic message on error
      messages.value = messages.value.filter((m) => m.id !== userMessage.id)
    } finally {
      isLoading.value = false
    }
  }

  function clearPendingAction() {
    pendingAction.value = null
  }

  function clearError() {
    error.value = null
  }

  /**
   * Clears the current thread and all conversation state.
   * Called when navigating to a different context (module/resource).
   */
  function clearCurrentThread() {
    currentThread.value = null
    messages.value = []
    streamingContent.value = ''
    currentToolCalls.value = []
    pendingAction.value = null
    error.value = null
    isStreaming.value = false
    isReadyToChat.value = false
  }

  /**
   * Sets the invocation context and clears the thread if context changed.
   * This is called from the router afterEach hook.
   */
  function setInvocationContext(newContext: RightyInvocationContext) {
    const contextChanged = !isSameContext(invocationContext.value, newContext)

    // Update the context
    invocationContext.value = newContext

    // Clear thread if navigating to a different context (module or resource changed)
    if (contextChanged) {
      clearCurrentThread()
    }
  }

  /**
   * Sets nested context for drawer content.
   * This overlays additional context on top of the base route context.
   * Called by drawer-hosting components when they open nested content.
   */
  function setNestedContext(nestedContext: RightyNestedContext) {
    if (invocationContext.value) {
      invocationContext.value = {
        ...invocationContext.value,
        nestedContext
      }
    }
  }

  /**
   * Clears nested context when drawer closes.
   * Called by drawer-hosting components on drawer hide.
   */
  function clearNestedContext() {
    if (invocationContext.value?.nestedContext) {
      const { nestedContext, ...baseContext } = invocationContext.value
      invocationContext.value = baseContext as RightyInvocationContext
    }
  }

  async function startNewConversation() {
    // Just set ready-to-chat mode - thread will be created when user sends first message
    currentThread.value = null
    messages.value = []
    isReadyToChat.value = true
  }

  async function deleteCurrentThread() {
    if (currentThread.value) {
      await deleteThread(currentThread.value.id)
    }
  }

  /**
   * Save a snapshot of the current config before executing a plan.
   * This allows the user to undo/restore if they don't like the result.
   */
  function saveConfigSnapshot() {
    const configStore = useConfigStore()
    if (configStore.config) {
      configSnapshotBeforeBuild.value = klona(toRaw(configStore.config))
    }
  }

  /**
   * Restore the config to its state before the last plan execution.
   * Returns true if restoration was successful, false if no snapshot exists.
   */
  function restoreConfigSnapshot(): boolean {
    if (!configSnapshotBeforeBuild.value) {
      return false
    }

    const configStore = useConfigStore()
    configStore.config = klona(configSnapshotBeforeBuild.value)

    // Clear snapshot and changelog after restore
    configSnapshotBeforeBuild.value = null
    lastBuildChangelog.value = []

    return true
  }

  /**
   * Clear the snapshot without restoring (e.g., when user confirms the changes are good)
   */
  function clearSnapshot() {
    configSnapshotBeforeBuild.value = null
    lastBuildChangelog.value = []
  }

  /**
   * Set the changelog from a build execution result
   */
  function setChangelog(changelog: ChangelogItem[]) {
    lastBuildChangelog.value = changelog
  }

  /**
   * Set the changelog for a specific message (after plan build completes).
   * This persists the changelog with the message so it stays visible in the thread.
   */
  function setMessageChangelog(messageId: number, changelog: ChangelogItem[]) {
    builtMessageChangelogs.value.set(messageId, changelog)
    // Also update lastBuildChangelog for backwards compatibility (getRecentCreationsForContext)
    lastBuildChangelog.value = changelog
  }

  /**
   * Get the changelog for a specific message, if it has been built.
   */
  function getMessageChangelog(messageId: number): ChangelogItem[] {
    return builtMessageChangelogs.value.get(messageId) || []
  }

  /**
   * Check if a message's plan has been built.
   */
  function isMessageBuilt(messageId: number): boolean {
    return builtMessageChangelogs.value.has(messageId)
  }

  /**
   * Get recent creations from the last build for passing to the AI.
   * This enables Righty to know what was just created so it can modify instead of duplicate.
   * Returns the last 5 relevant items (yesno, question, offer, dimension).
   */
  function getRecentCreationsForContext(): RecentCreation[] {
    return lastBuildChangelog.value
      .filter((item) => ['yesno', 'question', 'offer', 'dimension'].includes(item.resourceType))
      .slice(-5)
      .map((item) => ({
        resourceType: item.resourceType,
        name: item.name,
        id: item.id
      }))
  }

  /**
   * Report completed build actions to the backend for persistent storage.
   * This allows Claude to know what was already built across page refreshes
   * and long conversations, preventing it from recreating the same resources.
   */
  async function reportBuildCompletion(changelog: ChangelogItem[]) {
    // Skip if no actions to report
    if (!changelog || changelog.length === 0) return

    if (!teamPid.value || teamPid.value === 'demo' || !currentThread.value) return

    try {
      await api.post(
        `/api/${teamPid.value}/righty/threads/${currentThread.value.id}/actions`,
        { changelog }
      )
    } catch (err) {
      // Non-critical error - don't show to user, just log
      console.error('Failed to report build completion:', err)
    }
  }

  /**
   * Register dynamic prompts from a component.
   * Used for conditional prompts that depend on component state (e.g., visitor identification).
   * These prompts are merged with route-based prompts in RightyContextPrompts.
   */
  function registerDynamicPrompts(prompts: RightyPrompt[]) {
    dynamicPrompts.value = prompts
  }

  /**
   * Clear dynamic prompts.
   * Should be called when component unmounts or conditions change.
   */
  function clearDynamicPrompts() {
    dynamicPrompts.value = []
  }

  /**
   * Temporarily hide the floating UI (button + context prompts).
   * Used when components provide their own inline Righty interface.
   */
  function hideFloatingUi() {
    isFloatingUiHidden.value = true
  }

  /**
   * Show the floating UI again.
   * Should be called when component with inline Righty unmounts.
   */
  function showFloatingUi() {
    isFloatingUiHidden.value = false
  }

  /**
   * Activate the empty assistant when empty content is detected.
   * Shows a synthetic welcome message and opens the drawer.
   * Generic function that works for flows, emails, and other content types.
   *
   * @param type - The type of content (e.g., 'flow', 'email')
   * @param id - The ID of the resource
   * @param message - The welcome message to display
   * @param name - Optional name of the resource
   */
  function activateEmptyAssistant(type: string, id: string, message: string, name?: string) {
    isEmptyAssistantActive.value = true
    emptyAssistantContext.value = { type, id, name }
    syntheticWelcomeMessage.value = {
      id: -1,
      role: 'assistant',
      content: message,
      createdAt: new Date().toISOString()
    }
    isReadyToChat.value = true
    openDrawer()
  }

  /**
   * Deactivate the empty assistant.
   * Called when content is no longer empty or user navigates away.
   */
  function deactivateEmptyAssistant() {
    isEmptyAssistantActive.value = false
    syntheticWelcomeMessage.value = null
    emptyAssistantContext.value = null
  }

  // Legacy wrappers for backwards compatibility
  function activateEmptyFlowAssistant(flowId: string, flowName: string) {
    activateEmptyAssistant(
      'flow',
      flowId,
      "Hello! I'm here to help you make the perfect Flow. What are you trying to achieve?",
      flowName
    )
  }

  function deactivateEmptyFlowAssistant() {
    deactivateEmptyAssistant()
  }

  return {
    // State
    isDrawerOpen,
    isFloatingUiHidden,
    threads,
    currentThread,
    messages,
    isLoading,
    isStreaming,
    streamingContent,
    streamingStartTime,
    currentPhase,
    isResearching,
    pendingAction,
    error,
    currentToolCalls,
    isReadyToChat,
    invocationContext,
    dynamicPrompts: readonly(dynamicPrompts),
    configSnapshotBeforeBuild,
    lastBuildChangelog,
    builtMessageChangelogs,
    syntheticWelcomeMessage,
    isEmptyAssistantActive,
    emptyAssistantContext,
    // Type-specific computed aliases
    isEmptyFlowAssistantActive,
    isEmptyEmailAssistantActive,
    emptyFlowContext,

    // Computed
    hasThreads,
    currentPage,
    canUndo,
    contextualSuggestions,
    externalChipPrompts,

    // Actions
    openDrawer,
    closeDrawer,
    toggleDrawer,
    loadThreads,
    createThread,
    selectThread,
    deleteThread,
    sendMessage,
    sendMessageSync,
    clearPendingAction,
    clearError,
    clearCurrentThread,
    setInvocationContext,
    setNestedContext,
    clearNestedContext,
    startNewConversation,
    deleteCurrentThread,
    saveConfigSnapshot,
    restoreConfigSnapshot,
    clearSnapshot,
    setChangelog,
    setMessageChangelog,
    getMessageChangelog,
    isMessageBuilt,
    getRecentCreationsForContext,
    reportBuildCompletion,
    registerDynamicPrompts,
    clearDynamicPrompts,
    hideFloatingUi,
    showFloatingUi,
    activateEmptyAssistant,
    deactivateEmptyAssistant,
    // Legacy aliases
    activateEmptyFlowAssistant,
    deactivateEmptyFlowAssistant
  }
})
